import { useForm } from "react-hook-form";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { IStaffClinicRow } from "@/api_framework/api_modals/Configuration";
import { useStaffClinicMappingAPI } from "@/app/hooks/api_hooks/configuration/useStaffClinicMappingAPI";

const searchInputClass =
  "h-11 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-base text-slate-700 placeholder:text-gray-400 transition-colors focus:border-[#193A6A] focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15 sm:h-[38px] sm:text-xs";

const TYPE_LABELS: Record<string, string> = {
  groomer: "Groomer",
  doctor: "Doctor",
  paravet: "Paravet",
  clinic_manager: "Clinic Manager",
  assistant_clinic_manager: "Assistant Clinic Manager",
  housekeeping: "Housekeeping",
  area_manager: "Area Manager",
};

const ClinicMappingList = () => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { fetchClinics, fetchCityStaff, saveMapping, clinics, cityStaff, isLoading } = useStaffClinicMappingAPI();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClinic, setSelectedClinic] = useState<IStaffClinicRow | null>(null);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  const formHook = useForm<{ staff_ids: string[] }>({ mode: "onChange" });

  useEffect(() => {
    fetchClinics();
  }, [fetchClinics]);

  useEffect(() => {
    if (!selectedClinic) return;
    fetchCityStaff(selectedClinic.city_id);
    setSelectedStaffIds(selectedClinic.staff_ids || []);
    formHook.setValue("staff_ids", selectedClinic.staff_ids || []);
  }, [selectedClinic]);

  const staffItems = useMemo(
    () =>
      cityStaff.map((staff) => ({
        id: staff.staff_id,
        title: staff.display_name || staff.name,
        subtitle: TYPE_LABELS[staff.staff_type] || staff.staff_type,
      })),
    [cityStaff],
  );

  const selectedStaff = useMemo(
    () => staffItems.filter((item) => selectedStaffIds.includes(item.id)),
    [staffItems, selectedStaffIds],
  );

  const filteredClinics = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return clinics;
    return clinics.filter((clinic) => clinic.clinic_name?.toLowerCase().includes(query));
  }, [clinics, searchQuery]);

  const columns = [
    { header: "SN", cell: (_: any, index: number) => index + 1 },
    { header: "Clinic", accessor: "clinic_name" },
    {
      header: "Mapped staff",
      cell: (clinic: IStaffClinicRow) =>
        clinic.staff_count > 0 ? (
          <span className="text-slate-800">{clinic.staff_count}</span>
        ) : (
          <span className="text-[#6C727F]">None</span>
        ),
    },
    {
      header: "Action",
      cell: (clinic: IStaffClinicRow) => (
        <button
          type="button"
          className="rounded-md px-1 text-[#193A6A] transition-colors hover:underline focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15"
          onClick={() => setSelectedClinic(clinic)}
        >
          {clinic.staff_count > 0 ? "Edit mapping" : "Map staff"}
        </button>
      ),
    },
  ];

  const removeStaff = (staffId: string) => {
    const ids = selectedStaffIds.filter((id) => id !== staffId);
    setSelectedStaffIds(ids);
    formHook.setValue("staff_ids", ids);
  };

  const onSave = async () => {
    if (!selectedClinic) return;
    setIsSaving(true);
    const saved = await saveMapping(selectedClinic.id, selectedStaffIds);
    setIsSaving(false);
    if (saved) {
      setSelectedClinic(null);
      fetchClinics();
    }
  };

  return (
    <div className="px-5 py-4">
      <div>
        <h1 className="text-base font-semibold text-slate-900">Staff clinic mapping</h1>
        <p className="mt-1 text-xs text-[#6C727F]">
          Assign staff to the clinics they work at. Only staff whose profile includes a clinic&apos;s city can be mapped
          to it.
        </p>
      </div>

      <div className="relative mt-5 w-full sm:w-[320px]">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="Search clinic"
          aria-label="Search clinics"
          className={searchInputClass}
        />
      </div>

      <div className="mt-4">
        {isLoading ? (
          <>
            <SkeletonType1 />
            <SkeletonType1 className="mt-4" />
          </>
        ) : filteredClinics.length === 0 ? (
          <NoDataFoundComponent
            imageURL={NO_APPOINTMENTS}
            message="No clinics match this search"
            description="Clear the search to see every clinic."
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <DataTable data={filteredClinics} columns={columns} maxHeight="600px" />
          </div>
        )}
      </div>

      <Transition.Root show={Boolean(selectedClinic)} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={() => setSelectedClinic(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/40" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 translate-y-2"
                enterTo="opacity-100 translate-y-0"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-2"
              >
                <Dialog.Panel className="w-full max-w-xl overflow-visible rounded-xl bg-white shadow-xl">
                  <div className="border-b border-slate-100 px-5 py-4">
                    <Dialog.Title className="text-sm font-semibold text-slate-900">
                      {selectedClinic?.clinic_name}
                    </Dialog.Title>
                    <p className="mt-0.5 text-xs text-[#6C727F]">
                      {selectedStaff.length === 0 ? "No staff mapped" : `${selectedStaff.length} staff selected`}
                    </p>
                  </div>

                  <div className="px-5 py-4">
                    <label className="text-xs font-medium text-[#1F1F23]">Staff</label>
                    <div className="mt-1.5">
                      <SearchableMultiSelectMenu
                        withSelectAll
                        showTooltips
                        withDropDownSearch
                        errorMessages={[{ message: "", type: "required" }]}
                        fieldError={formHook.formState.errors.staff_ids}
                        register={formHook.register("staff_ids")}
                        showTypedErrors={false}
                        placeholder="Select staff"
                        defaultSelected={selectedStaff}
                        selectItems={staffItems}
                        onSelectItem={(items) => {
                          const ids = Array.from(new Set((items || []).map((item) => item.id)));
                          setSelectedStaffIds(ids);
                          formHook.setValue("staff_ids", ids);
                        }}
                        listBoxClassName="w-full"
                        containerClassName="w-full"
                      />
                    </div>
                    <p className="mt-2 text-[11px] leading-4 text-[#6C727F]">
                      {staffItems.length === 0
                        ? "No active staff are mapped to this clinic's city yet. Add the city to a staff profile first."
                        : `${staffItems.length} active staff available in this clinic's city.`}
                    </p>

                    {selectedStaff.length > 0 && (
                      <div className="mt-4 rounded-lg bg-slate-50 p-3">
                        <p className="text-xs font-medium text-slate-900">Mapped staff</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {selectedStaff.map((staff) => (
                            <span
                              key={staff.id}
                              className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-800 ring-1 ring-slate-200"
                            >
                              {staff.title}
                              <span className="text-[10px] font-normal text-[#6C727F]">{staff.subtitle}</span>
                              <button
                                type="button"
                                aria-label={`Remove ${staff.title}`}
                                className="text-slate-400 transition-colors hover:text-rose-500"
                                onClick={() => removeStaff(staff.id)}
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-5 py-4">
                    <button
                      type="button"
                      className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15"
                      onClick={() => setSelectedClinic(null)}
                    >
                      Cancel
                    </button>
                    <SolidButton size="2XL" disabled={isSaving} onButtonClick={onSave}>
                      {isSaving ? "Saving" : "Save mapping"}
                    </SolidButton>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default ClinicMappingList;
